import { create } from "zustand";
import { api } from "../api";
import { getStoredRoundId, setStoredRoundId } from "../lib/activeRound";
import type { GuessResult, RoundDetail, Settings } from "@shared/types";

export type GameStatus =
  | "idle"
  | "resuming"
  | "starting"
  | "playing"
  | "won"
  | "lost"
  | "revealed";

interface GameState {
  round: RoundDetail | null;
  guesses: GuessResult[];
  status: GameStatus;
  error: string | null;
  lastGuessId: string | null;
  revealedTarget: string | null;
  resume: () => Promise<void>;
  start: (override?: Partial<Settings>) => Promise<void>;
  guess: (text: string) => Promise<void>;
  giveUp: () => Promise<void>;
  timeOut: () => Promise<void>;
  reset: () => void;
}

export const useGameStore = create<GameState>((set, get) => ({
  round: null,
  guesses: [],
  status: getStoredRoundId() ? "resuming" : "idle",
  error: null,
  lastGuessId: null,
  revealedTarget: null,
  resume: async () => {
    const id = getStoredRoundId();
    if (!id) {
      set({ status: "idle" });
      return;
    }
    try {
      const r = await api.getRound(id);
      if (r.endedAt !== null) {
        setStoredRoundId(null);
        set({ status: "idle", round: null, guesses: [] });
        return;
      }
      set({
        round: r,
        guesses: r.guesses ?? [],
        status: "playing",
        lastGuessId: r.guesses?.[r.guesses.length - 1]?.id ?? null,
      });
    } catch {
      setStoredRoundId(null);
      set({ status: "idle" });
    }
  },
  start: async (override) => {
    set({
      status: "starting",
      error: null,
      guesses: [],
      lastGuessId: null,
      revealedTarget: null,
    });
    try {
      const r = await api.startRound(override);
      setStoredRoundId(r.id);
      set({ round: r, guesses: r.guesses ?? [], status: "playing" });
    } catch (e) {
      set({ status: "idle", error: e instanceof Error ? e.message : "error" });
      throw e;
    }
  },
  guess: async (text) => {
    const r = get().round;
    if (!r) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    const existing = get().guesses.find(
      (g) => g.text.toLowerCase() === trimmed.toLowerCase(),
    );
    if (existing) {
      set({ lastGuessId: existing.id });
      return;
    }
    try {
      const res = await api.guess(r.id, trimmed);
      const guesses = [...get().guesses, res];
      set({ guesses, lastGuessId: res.id });
      if (res.roundWon) {
        setStoredRoundId(null);
        set({ status: "won", revealedTarget: res.target ?? r.target ?? null });
      }
    } catch (e) {
      const err = e as Error & { code?: string };
      if (err.code === "expired") {
        await get().timeOut();
      } else {
        set({ error: err.message });
      }
    }
  },
  giveUp: async () => {
    const r = get().round;
    if (!r) return;
    const res = await api.reveal(r.id);
    setStoredRoundId(null);
    set({ status: "revealed", revealedTarget: res.target });
  },
  timeOut: async () => {
    const r = get().round;
    if (!r) return;
    try {
      const res = await api.timeout(r.id);
      setStoredRoundId(null);
      set({ status: "lost", revealedTarget: res.target });
    } catch {
      setStoredRoundId(null);
      set({ status: "lost" });
    }
  },
  reset: () => {
    setStoredRoundId(null);
    set({
      round: null,
      guesses: [],
      status: "idle",
      error: null,
      lastGuessId: null,
      revealedTarget: null,
    });
  },
}));
